import React, { useState, useEffect } from 'react';

const JobSeekerAssignments = () => {
  const [jobSeekers, setJobSeekers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ jobSeekerId: '', jobTitle: '', status: 'pending', reason: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const token = localStorage.getItem('token');

  // Fetch jobseekers
  useEffect(() => {
    const fetchJobSeekers = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}admin/admin/dashboard`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await response.json();
        if (response.ok) {
          setJobSeekers(data.jobSeekers || []);
        } else {
          setError(data.message || 'Failed to fetch jobseekers');
        }
      } catch (err) {
        setError('Server error while fetching jobseekers');
        console.error('Fetch jobseekers error:', err);
      }
    };

    if (token) {
      fetchJobSeekers();
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}admin/admin/assign-job/${formData.jobSeekerId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          jobTitle: formData.jobTitle,
          status: formData.status,
          reason: formData.status === 'rejected' ? formData.reason : undefined
        })
      });
      const data = await response.json();
      if (response.ok) {
        setSuccess(data.message);
        setFormData({ jobSeekerId: '', jobTitle: '', status: 'pending', reason: '' });
        setShowForm(false);
        // Refresh jobseekers
        const dashboardResponse = await fetch(`${process.env.REACT_APP_API_URL}admin/admin/dashboard`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const dashboardData = await dashboardResponse.json();
        if (dashboardResponse.ok) {
          setJobSeekers(dashboardData.jobSeekers || []);
        }
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError('Server error while assigning job');
      console.error('Assign job error:', err);
    }
  };

  return (
    <div className="p-6 bg-white text-gray-800">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold text-gray-800">Job Assignments</h2>
        <button
          className="bg-orange-500 text-white px-4 py-2 rounded hover:bg-orange-600 transition-colors"
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? 'Cancel' : 'Assign Job'}
        </button>
      </div>
      {showForm && (
        <form onSubmit={handleSubmit} className="mb-6 bg-gray-100 p-6 rounded-lg shadow-md">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <select
              value={formData.jobSeekerId}
              onChange={e => setFormData({ ...formData, jobSeekerId: e.target.value })}
              className="p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
              required
            >
              <option value="">Select JobSeeker</option>
              {jobSeekers.length > 0 ? (
                jobSeekers.map(jobSeeker => (
                  <option key={jobSeeker._id} value={jobSeeker._id}>
                    {jobSeeker.firstName} {jobSeeker.lastName} ({jobSeeker.email})
                  </option>
                ))
              ) : (
                <option value="" disabled>No jobseekers available</option>
              )}
            </select>
            <input
              type="text"
              placeholder="Job Title & Description"
              value={formData.jobTitle}
              onChange={e => setFormData({ ...formData, jobTitle: e.target.value })}
              className="p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
              required
            />
            <select
              value={formData.status}
              onChange={e => setFormData({ ...formData, status: e.target.value })}
              className="p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="pending">Pending</option>
              <option value="scheduled">Scheduled</option>
              <option value="accepted">Accepted</option>
              <option value="rejected">Rejected</option>
            </select>
            {formData.status === 'rejected' && (
              <input
                type="text"
                placeholder="Reason for Rejection"
                value={formData.reason}
                onChange={e => setFormData({ ...formData, reason: e.target.value })}
                className="p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            )}
          </div>
          <button
            type="submit"
            className="mt-4 bg-orange-500 text-white px-4 py-2 rounded hover:bg-orange-600 transition-colors"
          >
            Assign Job
          </button>
          {success && <p className="mt-2 text-green-600">{success}</p>}
          {error && <p className="mt-2 text-red-600">{error}</p>}
        </form>
      )}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-200">
              <th className="p-2 text-left text-gray-700">JobSeeker</th>
              <th className="p-2 text-left text-gray-700">Job Title</th>
              <th className="p-2 text-left text-gray-700">Status</th>
              <th className="p-2 text-left text-gray-700">Reason</th>
              <th className="p-2 text-left text-gray-700">Assigned At</th>
            </tr>
          </thead>
          <tbody>
            {jobSeekers.length > 0 ? (
              jobSeekers.flatMap(jobSeeker =>
                jobSeeker.jobAssignments?.map(assignment => (
                  <tr key={`${jobSeeker._id}-${assignment.assignedAt}`} className="border-b border-gray-200 hover:bg-gray-50">
                    <td className="p-2">{jobSeeker.firstName} {jobSeeker.lastName}</td>
                    <td className="p-2">{assignment.jobTitle}</td>
                    <td className="p-2">{assignment.status}</td>
                    <td className="p-2">{assignment.reason || '-'}</td>
                    <td className="p-2">{assignment.assignedAt ? new Date(assignment.assignedAt).toLocaleDateString() : '-'}</td>
                  </tr>
                ))
              )
            ) : (
              <tr>
                <td colSpan="5" className="p-2 text-gray-600 text-center">
                  No job assignments found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default JobSeekerAssignments;